import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  Video, 
  Loader2,
  BookOpen,
  ExternalLink,
  User,
  Tag,
  Edit2,
  X,
  Save
} from 'lucide-react';

const ContentManagerV2 = () => {
  const [activeSidebarItem, setActiveSidebarItem] = useState('articles');
  const [magIssues, setMagIssues] = useState([]);
  const [selectedEditionId, setSelectedEditionId] = useState('');
  const [articles, setArticles] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [error, setError] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [availableCategories, setAvailableCategories] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState(null);
  const [savingCategories, setSavingCategories] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
  const SUPABASE_STORAGE_BASE = 'https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs';

  // Build PDF URL from storage_path
  const getPdfUrl = (storagePath) => {
    if (!storagePath) return null;
    // URL encode each path segment separately to preserve slashes
    const encodedPath = storagePath.split('/').map(segment => encodeURIComponent(segment)).join('/');
    return `${SUPABASE_STORAGE_BASE}/${encodedPath}`;
  };

  // Fetch available categories
  const fetchAvailableCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v6/categories`);
      const data = await response.json();
      if (data.success) {
        setAvailableCategories(data.categories || {});
        console.log('Available categories loaded:', data.categories);
        // Debug: Log first category structure
        const firstMainCat = Object.keys(data.categories || {})[0];
        if (firstMainCat) {
          console.log(`Sample category structure for "${firstMainCat}":`, data.categories[firstMainCat]);
          console.log('First sub-category item:', data.categories[firstMainCat]?.[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [API_URL]);

  // Fetch magazine issues
  useEffect(() => {
    const fetchMagIssues = async () => {
      try {
        setLoadingIssues(true);
        setError(null);
        const response = await fetch(`${API_URL}/api/v6/mag-issues`);
        if (!response.ok) {
          throw new Error('Failed to fetch magazine issues');
        }
        const data = await response.json();
        if (data.success) {
          setMagIssues(data.issues || []);
        } else {
          throw new Error(data.error || 'Failed to fetch magazine issues');
        }
      } catch (err) {
        console.error('Error fetching magazine issues:', err);
        setError(err.message);
      } finally {
        setLoadingIssues(false);
      }
    };

    fetchMagIssues();
    fetchAvailableCategories();
  }, [API_URL, fetchAvailableCategories]);

  // Fetch articles when edition is selected
  useEffect(() => {
    if (selectedEditionId && activeSidebarItem === 'articles') {
      const fetchArticles = async () => {
        try {
          setLoadingArticles(true);
          setError(null);
          const response = await fetch(`${API_URL}/api/v6/content-articles?edition_id=${selectedEditionId}`);
          if (!response.ok) {
            throw new Error('Failed to fetch articles');
          }
          const data = await response.json();
          if (data.success) {
            setArticles(data.articles || []);
          } else {
            throw new Error(data.error || 'Failed to fetch articles');
          }
        } catch (err) {
          console.error('Error fetching articles:', err);
          setError(err.message);
        } finally {
          setLoadingArticles(false);
        }
      };

      fetchArticles();
    } else {
      setArticles([]);
    }
  }, [selectedEditionId, activeSidebarItem, API_URL]);

  const handleEditionChange = (e) => {
    setSelectedEditionId(e.target.value);
  };

  // Open edit categories modal
  const handleEditCategories = async (article) => {
    setEditingArticle(article);
    // Ensure categories are loaded
    if (Object.keys(availableCategories).length === 0) {
      await fetchAvailableCategories();
    }
    // Initialize selected categories from article.category_details
    const initialCategories = (article.category_details || []).map(cat => ({
      id: cat.id,
      main_category: cat.main_category,
      sub_category: cat.sub_category
    }));
    setSelectedCategories(initialCategories);
    setSelectedPrimaryCategory(null);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditingArticle(null);
    setSelectedCategories([]);
    setSelectedPrimaryCategory(null);
  };

  // Remove category from selected
  const handleRemoveCategory = (categoryId) => {
    setSelectedCategories(prev => prev.filter(cat => cat.id !== categoryId));
  };

  // Select primary category (just opens sub-categories section, doesn't auto-select General)
  const handleSelectPrimaryCategory = (mainCategory) => {
    setSelectedPrimaryCategory(mainCategory);
  };

  // Toggle sub-category selection
  const handleToggleSubCategory = (mainCategory, subCategory, categoryId) => {
    const exists = selectedCategories.some(cat => cat.id === categoryId);
    
    if (exists) {
      // Remove if already selected
      setSelectedCategories(prev => prev.filter(cat => cat.id !== categoryId));
    } else {
      // Add if not selected
      setSelectedCategories(prev => [...prev, {
        id: categoryId,
        main_category: mainCategory,
        sub_category: subCategory
      }]);
    }
  };

  // Save categories
  const handleSaveCategories = async () => {
    if (!editingArticle) return;
    
    try {
      setSavingCategories(true);
      const categoryIds = selectedCategories.map(cat => cat.id);
      
      const response = await fetch(`${API_URL}/api/v6/content-articles/${editingArticle.id}/categories`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category_ids: categoryIds })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save categories');
      }
      
      const data = await response.json();
      if (data.success) {
        // Refresh articles to show updated categories
        const articlesResponse = await fetch(`${API_URL}/api/v6/content-articles?edition_id=${selectedEditionId}`);
        const articlesData = await articlesResponse.json();
        if (articlesData.success) {
          setArticles(articlesData.articles || []);
        }
        handleCloseEditModal();
      } else {
        throw new Error(data.error || 'Failed to save categories');
      }
    } catch (err) {
      console.error('Error saving categories:', err);
      setError(err.message);
    } finally {
      setSavingCategories(false);
    }
  };

  // Get primary categories (where sub_category='general')
  const getPrimaryCategories = () => {
    const primaryCats = [];
    if (!availableCategories || Object.keys(availableCategories).length === 0) {
      console.log('No available categories loaded');
      return [];
    }
    Object.keys(availableCategories).forEach(mainCat => {
      const subCats = availableCategories[mainCat] || [];
      // Check if any sub-category has sub_category === 'General' (capital G as stored in DB)
      const hasGeneral = subCats.some(sub => {
        // Handle both object format {id, sub_category} and direct string
        const subCatName = sub && typeof sub === 'object' ? sub.sub_category : sub;
        if (typeof subCatName === 'string') {
          // Check for 'General' (capital G) as that's how it's stored in the database
          const isGeneral = subCatName.trim() === 'General' || subCatName.toLowerCase().trim() === 'general';
          if (isGeneral) {
            console.log(`Found General sub-category for ${mainCat}:`, sub);
          }
          return isGeneral;
        }
        return false;
      });
      if (hasGeneral) {
        primaryCats.push(mainCat);
        console.log(`Added primary category: ${mainCat}`);
      }
    });
    console.log('Primary categories found:', primaryCats);
    return primaryCats.sort();
  };

  // Get sub-categories for selected primary category (including General)
  const getSubCategoriesForPrimary = () => {
    if (!selectedPrimaryCategory) return [];
    // Return all sub-categories including General
    return availableCategories[selectedPrimaryCategory] || [];
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Content Manager</h2>
        </div>
        <nav className="mt-2">
          <button
            onClick={() => {
              setActiveSidebarItem('articles');
              setSelectedEditionId('');
            }}
            className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors ${
              activeSidebarItem === 'articles'
                ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-5 w-5" />
            <span className="font-medium">Articles</span>
          </button>
          <button
            onClick={() => {
              setActiveSidebarItem('webinars');
              setSelectedEditionId('');
            }}
            className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors ${
              activeSidebarItem === 'webinars'
                ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Video className="h-5 w-5" />
            <span className="font-medium">Webinars</span>
            <span className="ml-auto text-xs text-gray-400">(placeholder)</span>
          </button>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {activeSidebarItem === 'articles' && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Articles</h1>
              
              {/* Magazine Edition Dropdown */}
              <div className="mb-6">
                <label htmlFor="edition-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Magazine Edition
                </label>
                <select
                  id="edition-select"
                  value={selectedEditionId}
                  onChange={handleEditionChange}
                  className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 bg-white"
                  disabled={loadingIssues}
                >
                  <option value="">-- Select an edition --</option>
                  {magIssues.map((issue) => (
                    <option key={issue.id} value={issue.id}>
                      {issue.issue_name}
                    </option>
                  ))}
                </select>
                {loadingIssues && (
                  <p className="mt-2 text-sm text-gray-500 flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading editions...
                  </p>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Articles List */}
              {selectedEditionId && (
                <div>
                  {loadingArticles ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                      <span className="ml-3 text-gray-600">Loading articles...</span>
                    </div>
                  ) : articles.length > 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="divide-y divide-gray-200">
                        {articles.map((article) => (
                          <div
                            key={article.id}
                            className="p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {article.title || 'Untitled Article'}
                                </h3>
                                {article.sub_heading && (
                                  <p className="text-sm text-gray-600 mb-3">{article.sub_heading}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                  {article.author_name && (
                                    <span className="flex items-center space-x-1">
                                      <User className="h-4 w-4 text-gray-400" />
                                      <span className="font-medium text-gray-700">{article.author_name}</span>
                                    </span>
                                  )}
                                  {article.categories && article.categories.length > 0 && (
                                    <div className="flex items-center space-x-2 flex-wrap">
                                      <Tag className="h-4 w-4 text-gray-400" />
                                      <div className="flex flex-wrap gap-2">
                                        {article.category_details?.map((catDetail, idx) => (
                                          <span
                                            key={catDetail.id || idx}
                                            className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                                          >
                                            {catDetail.display || catDetail.main_category}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {article.pdf_storage_path && (
                                    <a
                                      href={getPdfUrl(article.pdf_storage_path)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center space-x-1 text-purple-600 hover:text-purple-800 transition-colors"
                                    >
                                      <FileText className="h-4 w-4" />
                                      <span>View PDF</span>
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => handleEditCategories(article)}
                                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                              >
                                <Edit2 className="h-4 w-4" />
                                <span>Edit</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                      <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No articles found for this edition.</p>
                    </div>
                  )}
                </div>
              )}

              {!selectedEditionId && !loadingIssues && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Please select a magazine edition to view articles.</p>
                </div>
              )}
            </div>
          )}

          {activeSidebarItem === 'webinars' && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Webinars</h1>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Webinar management coming soon...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Categories Modal */}
      {editingArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseEditModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Edit Categories</h2>
                  {editingArticle && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-900">{editingArticle.title || 'Untitled Article'}</p>
                      {editingArticle.author_name && (
                        <p className="text-xs text-gray-600">by {editingArticle.author_name}</p>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Selected Categories Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Selected</h3>
                {selectedCategories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedCategories.map((cat) => (
                      <span
                        key={cat.id}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-sm font-medium"
                      >
                        <span>{cat.sub_category && (cat.sub_category.trim() === 'General' || cat.sub_category.toLowerCase().trim() === 'general') ? cat.main_category : `${cat.main_category} > ${cat.sub_category}`}</span>
                        <button
                          onClick={() => handleRemoveCategory(cat.id)}
                          className="hover:opacity-75 hover:text-purple-900"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No categories selected</p>
                )}
              </div>

              {/* Primary Categories Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Primary Categories</h3>
                {getPrimaryCategories().length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {getPrimaryCategories().map((mainCat) => {
                    const isPrimarySelected = selectedPrimaryCategory === mainCat;
                    
                    return (
                      <button
                        key={mainCat}
                        onClick={() => handleSelectPrimaryCategory(mainCat)}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                          isPrimarySelected
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {mainCat}
                      </button>
                    );
                  })}
                </div>
                ) : (
                  <p className="text-sm text-gray-500">No primary categories available. Loading categories...</p>
                )}
              </div>

              {/* Sub-Categories Section */}
              {selectedPrimaryCategory && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Sub-Categories for {selectedPrimaryCategory}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {getSubCategoriesForPrimary().map((subCat) => {
                      const isSelected = selectedCategories.some(cat => cat.id === subCat.id);
                      return (
                        <button
                          key={subCat.id}
                          onClick={() => handleToggleSubCategory(selectedPrimaryCategory, subCat.sub_category, subCat.id)}
                          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                            isSelected
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {subCat.sub_category}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategories}
                  disabled={savingCategories}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {savingCategories ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagerV2;

